import { OAuthProvider } from '@seapoint/server/enums';
import { Navigate } from 'react-router';
import { Stack } from '../../design-system/stack/stack';
import { ConnectedOAuthProviderWrapper } from './components/connected-oauth-provider-wrapper';
import { OAuthConnectionTable } from './components/oauth-connection-table';
import { OAuthConnectionsTableWrapper } from './components/oauth-connections-table-wrapper';
import { XeroConnectedProviderDetails } from './components/xero-connected-provider-details';
import { AllowedOAuthProvider, OAuthConnectionInformation, OAuthProviderConnection } from './types/types';

const ACTIVE_CONNECTION_STATUS = 'CONNECTED';

type ProviderConfig =
  | { provider: undefined }
  | {
      provider: AllowedOAuthProvider;
      connectionStatus: OAuthConnectionInformation;
      onGoBack: () => void;
      onDisconnect: () => void;
      /** When true, shows a warning that data may be outdated due to rate limiting */
      isRateLimited?: boolean;
    };

type IntegrationsPageProps = {
  connections: OAuthProviderConnection[];
  providerConfig: ProviderConfig;
  onReviewInvoice: (connectionId: string) => void;
};

export const IntegrationsPage = ({ connections, providerConfig, onReviewInvoice }: IntegrationsPageProps) => {
  const activeConnections = connections.filter((c) => c.status === ACTIVE_CONNECTION_STATUS);
  const availableConnections = connections.filter((c) => c.status !== ACTIVE_CONNECTION_STATUS);

  if (providerConfig.provider && !providerConfig.connectionStatus?.connected) {
    return <Navigate to="/integrations" replace />;
  }

  return (
    <Stack align="stretch" className="min-h-screen py-2 pr-2">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-md border border-border bg-background shadow-xs">
        {providerConfig.provider &&
          (providerConfig.provider === OAuthProvider.XERO ? (
            <XeroConnectedProviderDetails
              connectionStatus={providerConfig.connectionStatus}
              onGoBack={providerConfig.onGoBack}
              onDisconnect={providerConfig.onDisconnect}
              isRateLimited={providerConfig.isRateLimited}
            />
          ) : (
            <ConnectedOAuthProviderWrapper {...providerConfig} />
          ))}

        {!providerConfig.provider && (
          <Stack space={10} align="stretch" className="px-6 pt-4">
            <h1 className="text-2xl leading-5 font-semibold tracking-tight text-black">Integrations</h1>

            {activeConnections.length > 0 && (
              <Stack space={10} align="stretch">
                <OAuthConnectionsTableWrapper title="Active Connections">
                  <OAuthConnectionTable connections={activeConnections} onReviewInvoice={onReviewInvoice} />
                </OAuthConnectionsTableWrapper>
              </Stack>
            )}

            {availableConnections.length > 0 && (
              <Stack space={10} align="stretch">
                <OAuthConnectionsTableWrapper title="Available Connections">
                  <OAuthConnectionTable connections={availableConnections} onReviewInvoice={onReviewInvoice} />
                </OAuthConnectionsTableWrapper>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
